/**
 * Branded content-identifier schemas — the foundation of the Orpheus type
 * system (architecture.md §3.1, decision D06).
 *
 * @remarks
 * Every content id family is a Zod-branded string type, so crossing families
 * (handing an {@link ItemId} to something expecting a {@link RoomId}) is a
 * compile error rather than a runtime mystery three saves later. Content ids
 * are globally unique within a pack (decision D11); `content-loader` enforces
 * that at load time.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

/**
 * Canonical id shape: snake_case, letter-first — e.g. `room_reactor_control`,
 * `item_plasma_cutter`, `flag_reactor_vented`.
 */
export const CONTENT_ID_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/u;

/**
 * Build a branded id schema for one content-id family.
 *
 * @param brandName - The nominal brand (e.g. `'RoomId'`) baked into the type
 * @returns A Zod schema that validates {@link CONTENT_ID_PATTERN} and brands its output
 */
const createContentIdSchema = <BrandName extends string>(brandName: BrandName) =>
	z
		.string()
		.regex(
			CONTENT_ID_PATTERN,
			`Invalid ${brandName}: content ids are snake_case and letter-first (e.g. "room_reactor_control")`,
		)
		.brand<BrandName>();

/**
 * D232 (actors round A): an actor's id — an embodied thing with a location,
 * a NEW entity kind beside items and rooms (Mike's ruling 1, 2026-10-04;
 * `docs/design/actors.md` §2). Its own family so that handing an actor where
 * an item is wanted is a compile error: an actor cannot be carried because
 * it is not an item, and the types say so before the handlers do.
 */
export const actorIdSchema = createContentIdSchema('ActorId');
export type ActorId = z.infer<typeof actorIdSchema>;

export const chapterIdSchema = createContentIdSchema('ChapterId');
export type ChapterId = z.infer<typeof chapterIdSchema>;

/**
 * D244: a counter's id — one integer the world (or the player) holds: the
 * air in a suit, the spares clipped to its back, the canisters a rack has
 * given. Its own family so that a counter can never be handed where a flag
 * is wanted: a flag is a fact, a counter is an amount, and the two are read
 * and written by different effects.
 */
export const counterIdSchema = createContentIdSchema('CounterId');
export type CounterId = z.infer<typeof counterIdSchema>;

/** R35 (D115): an ambience asset's id — the audio layer's pack-level table. */
export const audioIdSchema = createContentIdSchema('AudioId');
export type AudioId = z.infer<typeof audioIdSchema>;

export const endingIdSchema = createContentIdSchema('EndingId');
export type EndingId = z.infer<typeof endingIdSchema>;

export const eventDefinitionIdSchema = createContentIdSchema('EventDefinitionId');
export type EventDefinitionId = z.infer<typeof eventDefinitionIdSchema>;

export const exitIdSchema = createContentIdSchema('ExitId');
export type ExitId = z.infer<typeof exitIdSchema>;

export const flagIdSchema = createContentIdSchema('FlagId');
export type FlagId = z.infer<typeof flagIdSchema>;

/**
 * Pack-level game identifier (`game.config.json`'s `gameId`; the manifest's
 * pack id). Added to the D06 family in R1 — the manifest schema needs a
 * branded home for it like every other id.
 */
export const gameIdSchema = createContentIdSchema('GameId');
export type GameId = z.infer<typeof gameIdSchema>;

/** D217: a group of things a plural can name together — the jar house's four open jars. */
export const groupIdSchema = createContentIdSchema('GroupId');
export type GroupId = z.infer<typeof groupIdSchema>;

/**
 * Room-plate image identifier (R23/D89). Joined the D06 family with the
 * imagery layer's pack-level `images` table.
 */
export const imageIdSchema = createContentIdSchema('ImageId');
export type ImageId = z.infer<typeof imageIdSchema>;

export const itemIdSchema = createContentIdSchema('ItemId');
export type ItemId = z.infer<typeof itemIdSchema>;

/** R32: a scene sprite's id — the D96 contract's asset half. */
/**
 * D192: a declared speaker's id — the word a line's `source` uses. Never
 * one of the engine's four fixed sources, which the transcript schema owns;
 * the refinement here keeps the two vocabularies from overlapping at the
 * point the id is made.
 */
export const speakerIdSchema = createContentIdSchema('SpeakerId').refine(
	(id) => !['environment', 'human_log', 'player', 'system'].includes(id),
	{ message: 'A speaker id cannot be one of the engine\'s fixed sources (environment, human_log, player, system)' },
);
export type SpeakerId = z.infer<typeof speakerIdSchema>;

export const spriteIdSchema = createContentIdSchema('SpriteId');
export type SpriteId = z.infer<typeof spriteIdSchema>;

export const logIdSchema = createContentIdSchema('LogId');
export type LogId = z.infer<typeof logIdSchema>;

/**
 * D235 (actors round B2): a prompt's id — one question an actor asks the
 * player (`prompts[]` on an actor; `docs/design/actors.md` §4). Pack-wide
 * unique like every content id, because the asked ledger
 * (`narrative.askedPromptIds`) and the `ask_prompt` effect name it alone.
 */
export const promptIdSchema = createContentIdSchema('PromptId');
export type PromptId = z.infer<typeof promptIdSchema>;

/**
 * D235: one answer a prompt offers. Unique within its prompt only — the
 * `dialogue_answered` occurrence carries the prompt id beside it, so two
 * prompts may both offer `opt_yes`.
 */
export const promptOptionIdSchema = createContentIdSchema('PromptOptionId');
export type PromptOptionId = z.infer<typeof promptOptionIdSchema>;

export const puzzleIdSchema = createContentIdSchema('PuzzleId');
export type PuzzleId = z.infer<typeof puzzleIdSchema>;

export const roomIdSchema = createContentIdSchema('RoomId');
export type RoomId = z.infer<typeof roomIdSchema>;

export const sceneBindingIdSchema = createContentIdSchema('SceneBindingId');
export type SceneBindingId = z.infer<typeof sceneBindingIdSchema>;

/**
 * D231: a topic's id — one entry of a thing's keyed answer table (`topics`
 * on an item or a room; actors join in round A). Pack-wide unique like every
 * other content id, because the spent ledger (`narrative.spentTopicIds`) is
 * keyed by it alone.
 */
export const topicIdSchema = createContentIdSchema('TopicId');
export type TopicId = z.infer<typeof topicIdSchema>;

/**
 * Narrative-trigger identifier (R11). Joined the D06 family with the trigger
 * machinery — the family list in architecture.md §3.1 predates it.
 */
export const triggerIdSchema = createContentIdSchema('TriggerId');
export type TriggerId = z.infer<typeof triggerIdSchema>;

export const voiceLineIdSchema = createContentIdSchema('VoiceLineId');
export type VoiceLineId = z.infer<typeof voiceLineIdSchema>;
